import { useState } from 'react';
import { NavLink, Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { LayoutGrid, Users, ClipboardCheck, Route, ContactRound, LogOut, Menu, ChevronRight, ShieldCheck, UserRound } from 'lucide-react';
import { useAuth } from '../context/Auth';
import { Brand, SportLabel } from './Brand';
import { BRAND } from '../lib/brand';
import { AccountAvatar } from './AccountAvatar';
import { PassportNavigationContext } from './passports/PassportBrandBar';

const links = [
  ['/dashboard', 'Dashboard', LayoutGrid],
  ['/athletes', 'My Athletes', Users],
  ['/evaluations', 'Evaluation Setup', ClipboardCheck],
  ['/drills', 'Drill Plan Setup', Route],
  ['/passport', 'Trainer Passport', ContactRound],
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const parent = user.role === 'parent';
  const nav = parent ? [['/parent', 'Family Dashboard', LayoutGrid], ['/parent/profile', 'My Profile', UserRound]] : links;
  const playerPassport = /^\/parent\/athletes\/[^/]+\/passport$/.test(location.pathname);
  const trainerPassport = location.pathname === '/passport';
  const playerWorkspace = location.pathname.match(/^\/parent\/athletes\/([^/]+)$/);
  const current = playerPassport ? 'Player Passport' : location.pathname === '/parent/profile' ? 'My Profile' : nav.find(([to]) => location.pathname.startsWith(to))?.[1] || 'Athlete Workspace';

  return (
    <div className={`app-shell ${playerPassport ? 'pp-shell' : ''} ${trainerPassport ? 'tp-shell' : ''}`}>
      <aside className={`sidebar ${open ? 'sidebar-open' : ''}`}>
        <Link data-testid="sidebar-logo" to={parent ? '/parent' : '/dashboard'} aria-label={`${BRAND.name} home`}><Brand /></Link>
        <SportLabel id="sidebar-sport-label" className="sidebar-sport" />
        <div className="workspace-label" data-testid="workspace-role-label">{parent ? 'FAMILY WORKSPACE' : 'TRAINER WORKSPACE'}</div>
        <nav aria-label="Main navigation">
          {nav.map(([to, label, Icon]) => (
            <NavLink data-testid={`nav-${label.toLowerCase().replaceAll(' ', '-')}`} onClick={() => setOpen(false)} key={to} to={to} end={to === '/parent'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              <Icon size={18} /><span>{label}</span>{to === '/dashboard' && <span className="nav-active-dot" />}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <div className="privacy-note"><ShieldCheck size={18} /><span>Athlete privacy.<br /><strong>Always in your corner.</strong></span></div>
          <div className="user-menu">
            <AccountAvatar user={user} />
            <div><strong data-testid="signed-in-user">{user.name}</strong><small>{parent ? 'Parent account' : 'Trainer account'}</small></div>
            <button data-testid="logout-button" title="Sign out" onClick={async () => { await logout(); navigate('/login'); }}><LogOut size={17} /></button>
          </div>
        </div>
      </aside>
      {open && <button data-testid="mobile-menu-backdrop" className="mobile-backdrop" onClick={() => setOpen(false)} aria-label="Close navigation" />}
      <div className="main-wrap">
        <header className="topbar">
          <div className="breadcrumbs">
            <button className="mobile-menu" data-testid="mobile-menu-button" onClick={() => setOpen(!open)} aria-label="Open navigation"><Menu size={22} /></button>
            <span>{parent ? 'Family' : 'Workspace'}</span><ChevronRight size={13} /><strong data-testid="current-page-label">{current}</strong>
          </div>
          <div className="topbar-right">
            <SportLabel id="current-sport-label" className="topbar-sport" />
            <span className="system-status"><i /> ALL SYSTEMS GO</span>
            <AccountAvatar user={user} small />
          </div>
        </header>
        <main className="main-content">{parent && playerWorkspace && <div className="pp-workspace-entry"><Link className="action-btn" data-testid="open-player-passport" to={`/parent/athletes/${playerWorkspace[1]}/passport`}><ContactRound size={17} />Player Passport<ChevronRight size={15} /></Link></div>}<PassportNavigationContext.Provider value={{ openNavigation: () => setOpen(true) }}><Outlet /></PassportNavigationContext.Provider></main>
        <footer className="app-footer">
          <span data-testid="workspace-brand-footer">{BRAND.name.toUpperCase()} <span className="muted">/</span> BUILT FOR THE NEXT LEVEL.</span>
          <span>ONE ATHLETE. ONE REP. ONE STEP FORWARD.</span>
        </footer>
      </div>
    </div>
  );
}